import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';

const useWindowSize = () => {
  const isClient = typeof window === 'object';
  const getSize = () => ({
    windowWidth: isClient ? window.innerWidth : undefined,
    windowHeight: isClient ? window.innerHeight : undefined
  });
  const [windowSize, setWindowSize] = useState(getSize);
  useEffect(() => {
    if (!isClient) return;
    const handleResize = () => setWindowSize(getSize());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isClient]);
  return windowSize;
};

function computeContainBounds(cw, ch, nw, nh) {
  if (!cw || !ch || !nw || !nh) {
    return { left: 0, top: 0, width: cw || 0, height: ch || 0 };
  }
  const natAR = nw / nh;
  const boxAR = cw / ch;
  let rw;
  let rh;
  let ox;
  let oy;
  if (natAR > boxAR) {
    rw = cw;
    rh = cw / natAR;
    ox = 0;
    oy = (ch - rh) / 2;
  } else {
    rh = ch;
    rw = ch * natAR;
    ox = (cw - rw) / 2;
    oy = 0;
  }
  return { left: ox, top: oy, width: rw, height: rh };
}

function drawBoxesForImg(id, boxes) {
  const img = document.getElementById(`img-${id}`);
  const canvas = document.getElementById(`canvas-${id}`);
  if (!img || !canvas) return;
  if (!img.naturalWidth || !img.naturalHeight) return;

  const ctx = canvas.getContext('2d');
  const elemW = img.offsetWidth;
  const elemH = img.offsetHeight;

  canvas.width = elemW;
  canvas.height = elemH;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = 'rgba(255, 0, 0, 0.4)';

  if (boxes?.bboxes && Array.isArray(boxes.bboxes)) {
    const natAR = img.naturalWidth / img.naturalHeight;
    const elemAR = elemW / elemH;
    const objectFit = window.getComputedStyle(img).objectFit;

    let renderW;
    let renderH;
    let offsetX;
    let offsetY;

    if (objectFit === 'cover') {
      if (natAR > elemAR) {
        renderH = elemH;
        renderW = elemH * natAR;
      } else {
        renderW = elemW;
        renderH = elemW / natAR;
      }
      offsetX = (elemW - renderW) / 2;
      offsetY = (elemH - renderH) / 2;
    } else {
      if (natAR > elemAR) {
        renderW = elemW;
        renderH = elemW / natAR;
        offsetX = 0;
        offsetY = (elemH - renderH) / 2;
      } else {
        renderH = elemH;
        renderW = elemH * natAR;
        offsetX = (elemW - renderW) / 2;
        offsetY = 0;
      }
    }

    boxes.bboxes.forEach((box) => {
      const x = offsetX + box.x * renderW;
      const y = offsetY + box.y * renderH;
      const w = box.w * renderW;
      const h = box.h * renderH;
      ctx.fillRect(x, y, w, h);
    });
  }
}

const PreviewItem = ({
  id,
  previewUrl,
  imgStatus,
  idBackend,
  boxes,
  onDeleteImage,
}) => {
  const shellRef = useRef(null);
  const imgRef = useRef(null);
  const [bounds, setBounds] = useState({ left: 0, top: 0, width: '100%', height: '100%' });

  const measure = useCallback(() => {
    const shell = shellRef.current;
    const img = imgRef.current;
    if (!shell) return;
    const cw = shell.clientWidth;
    const ch = shell.clientHeight;
    if (!img?.naturalWidth) {
      setBounds({ left: 0, top: 0, width: cw, height: ch });
      return;
    }
    setBounds(computeContainBounds(cw, ch, img.naturalWidth, img.naturalHeight));
  }, []);

  useLayoutEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(shell);
    return () => ro.disconnect();
  }, [measure, previewUrl]);

  useLayoutEffect(() => {
    const img = imgRef.current;
    if (!img?.naturalWidth) return;
    drawBoxesForImg(id, boxes);
  }, [id, boxes, bounds]);

  const fitStyle =
    typeof bounds.width === 'number'
      ? {
          position: 'absolute',
          left: bounds.left,
          top: bounds.top,
          width: bounds.width,
          height: bounds.height,
        }
      : {
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
        };

  return (
    <div className="previewItem">
      {(imgStatus === 'done' || imgStatus === 'error' || imgStatus === 'sinDatos') && (
        <button
          type="button"
          onClick={() => onDeleteImage(id, idBackend)}
          style={{
            position: 'absolute',
            top: 10,
            right: 15,
            zIndex: 10,
            backgroundColor: 'rgba(0,0,0,0.6)',
            border: 'none',
            color: 'white',
            borderRadius: '50%',
            width: '22px',
            height: '22px',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          }}
          title="Eliminar"
        >
          ×
        </button>
      )}

      <div ref={shellRef} className="previewImageShell">
        <div className="previewImageBounds" style={fitStyle}>
          <img
            ref={imgRef}
            id={`img-${id}`}
            src={previewUrl}
            alt={`Preview ${id}`}
            className="previewImage"
            onLoad={measure}
            style={{
              filter: (imgStatus === 'toUpload' || imgStatus === 'borrando')
                ? 'grayscale(80%) blur(1px)'
                : 'none',
            }}
          />
        </div>
        <canvas id={`canvas-${id}`} className="previewCanvas" style={fitStyle} />
      </div>

      {(imgStatus === 'error' || imgStatus === 'sinDatos') && (
        <div
          style={{
            position: 'absolute',
            bottom: 5,
            left: 5,
            backgroundColor: '#EF4444',
            color: 'white',
            padding: '2px 6px',
            borderBottomLeftRadius: '8px',
            fontSize: '9px',
            fontWeight: '800',
            zIndex: 3,
          }}
        >
          {imgStatus === 'sinDatos' ? 'SIN DATOS' : 'ERROR'}
        </div>
      )}

      {(imgStatus === 'toUpload' || imgStatus === 'borrando') && (
        <div className="previewSpinner">
          <i className="fas fa-spinner fa-spin fa-2x"></i>
        </div>
      )}
    </div>
  );
};

const PreviewImgsNew = ({ imagesData, onDeleteImage }) => {
  const { windowWidth } = useWindowSize();
  const count = imagesData?.length ?? 0;
  const isMobileViewport = (windowWidth ?? 0) <= 576;
  const enableMobileScroll = isMobileViewport && count > 6;

  const columns = useMemo(() => {
    if (isMobileViewport) {
      if (count <= 1) return 1;
      if (count <= 4) return 2;
      return 3;
    }
    if (count <= 1) return 1;
    if (count === 2) return 2;
    if (count === 4) return 2;
    if (count >= 5) return 3;
    return 3;
  }, [count, isMobileViewport]);

  return (
    <div
      className={`previewImgs ${enableMobileScroll ? 'previewImgs--mobileScroll' : ''}`}
      style={{ width: '100%' }}
    >
      <div
        className="previewGrid"
        data-count={count}
        data-mobile-cols={columns}
        style={{
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        }}
      >
        {[...imagesData].reverse().map(({ id, previewUrl, imgStatus, idBackend, boxes }) => (
          <PreviewItem
            key={id}
            id={id}
            previewUrl={previewUrl}
            imgStatus={imgStatus}
            idBackend={idBackend}
            boxes={boxes}
            onDeleteImage={onDeleteImage}
          />
        ))}
      </div>
    </div>
  );
};

export default PreviewImgsNew;
